/**
 * Unified Date Formatting Utilities
 * جميع التواريخ في التطبيق تعرض بالتقويم الميلادي (Gregorian)
 *
 * This file provides consistent date formatting across the entire application.
 * All dates are displayed in Gregorian calendar format as per application requirements.
 */

// Base configuration for all date formatting
const DEFAULT_LOCALE = 'en-US';
const GREGORIAN_CALENDAR = 'gregory';

/**
 * Validates if a date string is valid
 */
export function isValidDate (dateString: string): boolean {
  if (!dateString) {return false;}
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

/**
 * Safe date parsing with fallback
 */
function parseDate (dateString: string): Date | null {
  if (!dateString) {return null;}
  const date = new Date(dateString);
  return isValidDate(dateString) ? date : null;
}

/**
 * UNIFIED DATE FORMATS - Use these throughout the application
 */

/**
 * Standard date format: DD/MM/YYYY
 * Use for: Tables, lists, general date display
 * Example: "02/10/2025"
 */
export function formatDate (dateString: string): string {
  const date = parseDate(dateString);
  if (!date) {return '-';}

  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    calendar: GREGORIAN_CALENDAR
  });
}

/**
 * Standard datetime format: DD/MM/YYYY HH:MM AM/PM
 * Use for: Timestamps, logs, detailed views
 * Example: "02/10/2025 12:03 PM"
 */
export function formatDateTime (dateString: string): string {
  const date = parseDate(dateString);
  if (!date) {return '-';}

  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    calendar: GREGORIAN_CALENDAR,
    hour12: true
  });
}

/**
 * Full date format with month name: Month DD, YYYY
 * Use for: User details, modals, important dates
 * Example: "October 2, 2025"
 */
export function formatDateFull (dateString: string): string {
  const date = parseDate(dateString);
  if (!date) {return '-';}

  return date.toLocaleDateString(DEFAULT_LOCALE, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    calendar: GREGORIAN_CALENDAR
  });
}

/**
 * Full datetime format with month name: Month DD, YYYY at HH:MM AM/PM
 * Use for: User details, modals, detailed timestamps
 * Example: "October 2, 2025 at 12:03 PM"
 */
export function formatDateTimeFull (dateString: string): string {
  const date = parseDate(dateString);
  if (!date) {return '-';}

  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    calendar: GREGORIAN_CALENDAR,
    hour12: true
  });
}

/**
 * Time only format: HH:MM AM/PM
 * Use for: Time displays, schedules
 * Example: "12:03 PM"
 */
export function formatTime (dateString: string): string {
  const date = parseDate(dateString);
  if (!date) {return '-';}

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    calendar: GREGORIAN_CALENDAR,
    hour12: true
  });
}

/**
 * Relative time format: "X days ago", "X hours ago", etc.
 * Use for: Recent activities, notifications
 * Example: "2 hours ago", "3 days ago"
 */
export function formatRelativeTime (dateString: string): string {
  const date = parseDate(dateString);
  if (!date) {return '-';}

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {return 'Just now';}
  if (diffInSeconds < 3600) {return `${Math.floor(diffInSeconds / 60)} minutes ago`;}
  if (diffInSeconds < 86400) {return `${Math.floor(diffInSeconds / 3600)} hours ago`;}
  if (diffInSeconds < 2592000) {return `${Math.floor(diffInSeconds / 86400)} days ago`;}

  return formatDate(dateString);
}

/**
 * DEPRECATED FUNCTIONS - Keep for backward compatibility
 * These will be removed in future versions
 */

/** @deprecated Use formatDate() instead */
export const formatDateOnly = formatDate;

/** @deprecated Use formatDateTime() instead */
export const formatGregorianDateTime = formatDateTime;

/** @deprecated Use formatDateTimeFull() instead */
export const formatGregorianDateWithMonthName = formatDateTimeFull;

/** @deprecated Use formatDate() instead */
export const formatGregorianDate = formatDate;
